// De proefrit: showroomafspraken via het Renoco-portaal.
// Twee standen, bepaald door de server (zie ShowroomBookingController): "direct" boekt live in de agenda,
// "aanvraag" biedt vaste tijden aan en een medewerker belt om te bevestigen.
// Eén store voor de hele site; elke "Afspraak maken"-knop opent hetzelfde
// paneel, eventueel met een dag en tijd al gekozen (vanuit de hero).
// Bij direct boeken kiest de bezoeker eerst een product (dezelfde lijst als het menu) of "weet ik nog niet".
// Pas daarna komen de momenten: het portaal geeft alleen de momenten van de adviseurs die dat product doen,
// dus een gekozen moment is altijd vrij bij een adviseur die het product kent.

import { inZin, landcode, t, voorvoegsel } from './taal';

// De gegevens van de bezoeker. Naam, telefoon en adres zijn verplicht; het land staat op de Duitse site op Duitsland.
const leegFormulier = () => ({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    street: '',
    house_number: '',
    zip_code: '',
    city: '',
    country: voorvoegsel === '/de' ? 'DE' : 'NL',
    message: '',
});

const KORTE_DAG = new Intl.DateTimeFormat(landcode, { weekday: 'short', timeZone: 'Europe/Amsterdam' });
const DAG_NUMMER = new Intl.DateTimeFormat(landcode, { day: 'numeric', timeZone: 'Europe/Amsterdam' });
const KORTE_MAAND = new Intl.DateTimeFormat(landcode, { month: 'short', timeZone: 'Europe/Amsterdam' });
const LANGE_DATUM = new Intl.DateTimeFormat(landcode, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Amsterdam' });

function datumVan(iso) {
    // Middag gebruiken voorkomt dat een tijdzone de datum een dag verschuift.
    return new Date(`${iso}T12:00:00Z`);
}

function relatief(iso) {
    const vandaag = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(new Date());
    const morgenDatum = new Date(`${vandaag}T12:00:00Z`);
    morgenDatum.setUTCDate(morgenDatum.getUTCDate() + 1);
    const morgen = morgenDatum.toISOString().slice(0, 10);

    if (iso === vandaag) return t('Vandaag');
    if (iso === morgen) return t('Morgen');
    return null;
}

function icsTekst(waarde) {
    return String(waarde).replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}

export default function maakBoeking() {
    return {
        open: false,
        status: 'idle', // idle | laden | klaar | mislukt
        dagen: [],
        modus: null, // null (nog niet geladen) | direct | aanvraag
        dag: null,
        tijd: null,
        stap: 'moment', // product (alleen bij direct boeken) | moment | gegevens | bevestigd
        bezig: false,
        fout: '',
        interesses: [],
        productgroepen: [], // de producten die het portaal aanbiedt (alleen bij direct boeken): [{ id, name, group }]
        productgroep: null, // gekozen id, of null voor "weet ik nog niet"
        productGekozen: false, // de bezoeker (of de productpagina) heeft een keuze gemaakt
        wisselt: false, // er worden momenten voor een andere productgroep opgehaald
        melding: '', // uitleg als het gewenste moment voor het gekozen product niet vrij is
        _gewenst: null, // moment waarmee het paneel werd geopend, in afwachting van de productkeuze
        form: leegFormulier(),
        bevestigd: null,
        bron: '',
        _laadBelofte: null,
        _voorkeurToegepast: false,
        _productVanPaginaToegepast: false,

        // Haalt de momenten op voor de gekozen productgroep. Geeft terug of dat gelukt is.
        _haalOp() {
            const query = this.productgroep ? `?product_group_id=${encodeURIComponent(this.productgroep)}` : '';

            return fetch(`${voorvoegsel}/showroom-booking/slots${query}`, { headers: { Accept: 'application/json' } })
                .then((response) => response.json().then((data) => ({ response, data })))
                .then(({ response, data }) => {
                    const gelukt = Boolean(response.ok && data.success);

                    if (gelukt) {
                        this.modus = data.mode === 'aanvraag' ? 'aanvraag' : 'direct';
                        this.productgroepen = Array.isArray(data.product_groups) ? data.product_groups : [];

                        // Biedt het portaal de gekozen groep niet (meer) aan, dan zijn dit de momenten zonder keuze.
                        if (this.productgroep !== null && data.product_group_id !== this.productgroep) {
                            this.productgroep = null;
                        }
                    }

                    this.dagen = (gelukt ? (data.days ?? []) : []).filter((dag) => Array.isArray(dag.slots) && dag.slots.length > 0);

                    return gelukt;
                });
        },

        laad() {
            if (this.status === 'klaar' || this._laadBelofte) {
                return this._laadBelofte ?? Promise.resolve();
            }

            this.status = 'laden';
            this._laadBelofte = this._haalOp()
                // Op een productpagina staat de productgroep van die pagina al gekozen.
                .then((gelukt) => (gelukt && this._productVanPagina() ? this._haalOp() : gelukt))
                .then((gelukt) => {
                    // Een gekozen productgroep zonder vrije momenten is geen storing: de bezoeker kan anders kiezen.
                    this.status = gelukt && (this.dagen.length || this.productgroep !== null) ? 'klaar' : 'mislukt';
                    this._beginstap();
                })
                .catch(() => {
                    this.status = 'mislukt';
                })
                .finally(() => {
                    this._laadBelofte = null;
                });

            return this._laadBelofte;
        },

        // Kiest de productgroep die bij de huidige pagina hoort, als het portaal die aanbiedt.
        _productVanPagina() {
            if (this._productVanPaginaToegepast || this.productgroep !== null || !this.productgroepen.length) {
                return false;
            }

            this._productVanPaginaToegepast = true;

            const namen = [...document.querySelectorAll('[data-productkeuze] [data-voorkeur]')]
                .map((el) => (el.dataset.product ?? '').trim().toLowerCase())
                .filter(Boolean);
            const groep = this.productgroepen.find((kandidaat) => namen.includes(String(kandidaat.name).trim().toLowerCase()));

            if (!groep) {
                return false;
            }

            this.productgroep = groep.id;
            this.productGekozen = true;
            this._interesseBijProductgroep();

            return true;
        },

        // Kan de bezoeker een product kiezen? Alleen bij direct boeken, als het portaal een lijst geeft.
        get metProductkeuze() {
            return this.productgroepen.length > 0;
        },

        // Zonder productkeuze begint de bezoeker bij "Waar komt u voor?".
        _beginstap() {
            if (this.stap === 'moment' && this.metProductkeuze && !this.productGekozen) {
                this.stap = 'product';
            }
        },

        // Zet het gewenste moment klaar als het (nog) vrij is. Geeft terug of dat gelukt is.
        _pasMomentToe(gewenst) {
            const dag = gewenst ? this.dagen.find((kandidaat) => kandidaat.date === gewenst.datum) : null;

            if (dag && (!gewenst.tijd || dag.slots.some((slot) => slot.time === gewenst.tijd))) {
                this.dag = gewenst.datum;
                this.tijd = gewenst.tijd ?? null;
                this.stap = this.tijd ? 'gegevens' : 'moment';

                return true;
            }

            if (!this.dagen.some((kandidaat) => kandidaat.date === this.dag)) {
                this.dag = this.dagen[0]?.date ?? null;
            }

            return false;
        },

        // De gekozen productgroep staat ook bij "interesse" aangevinkt, als het product daar met dezelfde naam staat.
        _interesseBijProductgroep() {
            const naam = this.productgroepNaam.trim().toLowerCase();
            if (!naam) return;

            const chip = [...document.querySelectorAll('[data-productkeuze] [data-product]')]
                .find((el) => (el.dataset.product ?? '').trim().toLowerCase() === naam);

            if (chip && !this.interesses.includes(chip.dataset.product)) {
                this.interesses.push(chip.dataset.product);
            }
        },

        // id van een productgroep, of null voor "weet ik nog niet". Daarna volgen de momenten voor die keuze.
        kiesProductgroep(id) {
            if (this.wisselt) {
                return Promise.resolve();
            }

            const zelfdeKeuze = this.productgroep === id && this.status === 'klaar';
            const gewenst = this._gewenst;

            this.productgroep = id;
            this.productGekozen = true;
            this._productVanPaginaToegepast = true;
            this._gewenst = null;
            this._interesseBijProductgroep();
            this.tijd = null;
            this.fout = '';
            this.melding = '';

            const verder = () => {
                const past = this._pasMomentToe(gewenst);

                // Het moment uit de hero is voor dit product niet vrij: de bezoeker kiest een ander moment.
                if (gewenst?.tijd && !past && this.dagen.length) {
                    this.melding = this.productgroepNaam
                        ? t('Dat moment is voor :product niet vrij. Kies hieronder een ander moment.', { product: inZin(t(this.productgroepNaam)) })
                        : t('Dat moment is niet meer vrij. Kies hieronder een ander moment.');
                }

                if (this.stap === 'product') {
                    this.stap = 'moment';
                }
            };

            // Dezelfde keuze als waarvoor de momenten al geladen zijn: niet opnieuw ophalen.
            if (zelfdeKeuze) {
                verder();

                return Promise.resolve();
            }

            this.wisselt = true;

            return this._haalOp()
                .then((gelukt) => {
                    if (!gelukt) {
                        this.status = 'mislukt';
                        this.stap = 'moment';
                        return;
                    }

                    verder();
                })
                .catch(() => {
                    this.status = 'mislukt';
                    this.stap = 'moment';
                })
                .finally(() => {
                    this.wisselt = false;
                });
        },

        terugNaarProduct() {
            this.stap = 'product';
            this.fout = '';
            this.melding = '';
        },

        // De producten onder hun groep uit het menu, in de volgorde van het portaal.
        get productgroepenPerGroep() {
            const secties = [];

            for (const product of this.productgroepen) {
                const laatste = secties[secties.length - 1];

                if (laatste && laatste.groep === (product.group ?? '')) {
                    laatste.producten.push(product);
                } else {
                    secties.push({ groep: product.group ?? '', producten: [product] });
                }
            }

            return secties;
        },

        // De namen van producten komen in het Nederlands uit het portaal; op /de en /en staan ze in de taal van de
        // bezoeker (de vertaling komt uit het menu, zie de tag taal:js). Naar het portaal gaat altijd de Nederlandse.
        vertaal(naam) {
            return naam ? t(naam) : '';
        },

        inZin(naam) {
            return naam ? inZin(t(naam)) : '';
        },

        get productgroepNaam() {
            return this.productgroepen.find((groep) => groep.id === this.productgroep)?.name ?? '';
        },

        // Geladen, maar voor de gekozen productgroep is er de komende weken niets vrij.
        get leeg() {
            return this.status === 'klaar' && this.dagen.length === 0;
        },

        herlaad() {
            this.status = 'idle';
            this.dagen = [];
            return this.laad();
        },

        // De eerstvolgende vrije momenten: per dag het eerste moment en een middagmoment,
        // zodat de bezoeker spreiding ziet in plaats van één volle ochtend.
        eerstvolgende(aantal = 4) {
            const lijst = [];
            const voegToe = (dag, slot) => lijst.push({
                datum: dag.date,
                tijd: slot.time,
                dagLabel: this.dagKort(dag.date),
                label: `${this.dagKort(dag.date)} ${slot.time}`,
                voluit: t(':dag om :tijd', { dag: this.dagLang(dag.date), tijd: slot.time }),
            });

            for (const dag of this.dagen) {
                if (lijst.length >= aantal) break;
                const eerste = dag.slots[0];
                voegToe(dag, eerste);

                const middag = dag.slots.find((slot) => slot.time >= '13:00' && slot.time !== eerste.time);
                if (middag && lijst.length < aantal) {
                    voegToe(dag, middag);
                }
            }

            return lijst.slice(0, aantal);
        },

        dagKort(iso) {
            const rel = relatief(iso);
            if (rel) return rel;
            const datum = datumVan(iso);
            return `${KORTE_DAG.format(datum).replace('.', '')} ${DAG_NUMMER.format(datum)} ${KORTE_MAAND.format(datum).replace('.', '')}`;
        },

        dagWeek(iso) {
            const rel = relatief(iso);
            return rel ?? KORTE_DAG.format(datumVan(iso)).replace('.', '');
        },

        dagNummer(iso) {
            return DAG_NUMMER.format(datumVan(iso));
        },

        dagMaand(iso) {
            return KORTE_MAAND.format(datumVan(iso)).replace('.', '');
        },

        dagLang(iso) {
            return LANGE_DATUM.format(datumVan(iso));
        },

        // In de stand "aanvraag" staat de afspraak nog niet vast: een medewerker belt om te bevestigen.
        get aanvraag() {
            return this.modus === 'aanvraag';
        },

        get gekozenDag() {
            return this.dagen.find((dag) => dag.date === this.dag) ?? null;
        },

        get samenvatting() {
            if (!this.dag || !this.tijd) return '';

            const moment = t(':dag om :tijd', { dag: this.dagLang(this.dag), tijd: this.tijd });

            return this.productgroepNaam
                ? t(':moment, voor :product', { moment, product: inZin(t(this.productgroepNaam)) })
                : moment;
        },

        openen(opties = {}) {
            this.bron = opties.bron ?? '';
            this.fout = '';

            if (this.stap === 'bevestigd') {
                this.reset();
            }

            this.open = true;
            this.voorkeurToepassen();

            this.laad().then(() => {
                const gewenst = opties.datum ? { datum: opties.datum, tijd: opties.tijd ?? null } : null;

                // Eerst het product: het gekozen moment blijft bewaard tot de bezoeker gekozen heeft.
                if (this.metProductkeuze && !this.productGekozen) {
                    this._gewenst = gewenst;
                    this.stap = 'product';
                    return;
                }

                this._pasMomentToe(gewenst);
            });

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: 'showroom_afspraak_open', bron: this.bron || 'knop' });
        },

        // Voor een boekingsblok dat inline op de pagina staat (showroompagina).
        klaarzetten() {
            this.voorkeurToepassen();

            return this.laad().then(() => {
                this._beginstap();

                if (!this.dag && this.dagen.length) {
                    this.dag = this.dagen[0].date;
                }
            });
        },

        // Een link uit een mail of een antwoord van de klantenservice opent het afspraakvenster meteen:
        // ?afspraak=1, met &afspraakproduct=<id uit het portaal> als de medewerker het product al koos.
        // De link in de mail is /afspraak?product=<id>; die stuurt door naar de pagina van dat product.
        openenVanuitLink() {
            const parameters = new URLSearchParams(window.location.search);

            if (!parameters.has('afspraak')) {
                return;
            }

            const product = Number.parseInt(parameters.get('afspraakproduct') ?? '', 10);

            this.openen({ bron: 'link' });

            if (!Number.isInteger(product)) {
                return;
            }

            // Na het laden: alleen kiezen als het portaal dit product (nog) aanbiedt, anders kiest de bezoeker zelf.
            this.laad().then(() => {
                if (this.productgroepen.some((groep) => groep.id === product)) {
                    this.kiesProductgroep(product);
                }
            });
        },

        sluiten() {
            this.open = false;
            if (this.stap === 'bevestigd') {
                setTimeout(() => this.reset(), 400);
            }
        },

        kiesDag(iso) {
            this.dag = iso;
            this.tijd = null;
            this.melding = '';
        },

        kiesTijd(tijd) {
            this.tijd = tijd;
            this.melding = '';
            this.stap = 'gegevens';
        },

        terugNaarMoment() {
            this.stap = 'moment';
            this.fout = '';
        },

        wisselInteresse(product) {
            const index = this.interesses.indexOf(product);
            if (index === -1) {
                this.interesses.push(product);
                return;
            }

            this.interesses.splice(index, 1);
        },

        // Op een productpagina staat dat product (en de groep) al aangevinkt.
        voorkeurToepassen() {
            if (this._voorkeurToegepast) return;
            this._voorkeurToegepast = true;

            const namen = [...document.querySelectorAll('[data-productkeuze] [data-voorkeur]')]
                .map((el) => el.dataset.product)
                .filter(Boolean);

            for (const naam of namen) {
                if (!this.interesses.includes(naam)) {
                    this.interesses.push(naam);
                }
            }
        },

        reset() {
            this.stap = 'moment';
            this.tijd = null;
            this.fout = '';
            this.bevestigd = null;
            this.interesses = [];
            this.productgroep = null;
            this.productGekozen = false;
            this.melding = '';
            this._gewenst = null;
            this._voorkeurToegepast = false;
            this._productVanPaginaToegepast = false;
            this.form = leegFormulier();
        },

        async boek() {
            if (!this.dag || !this.tijd || this.bezig) return;

            // Onder volledige statische caching ververst Statamic het token in de meta-tag.
            const csrf = document.querySelector('meta[name="csrf-token"]')?.content ?? '';

            this.bezig = true;
            this.fout = '';

            const bericht = [
                this.interesses.length ? `Interesse: ${this.interesses.join(', ')}.` : '',
                this.form.message.trim(),
                typeof window !== 'undefined' ? `Geboekt via: ${window.location.pathname}` : '',
            ].filter(Boolean).join(' ').slice(0, 1000);

            try {
                const response = await fetch(`${voorvoegsel}/showroom-booking/book`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': csrf,
                    },
                    body: JSON.stringify({
                        date: this.dag,
                        time: this.tijd,
                        ...this.form,
                        message: bericht,
                        // Los meegestuurd voor de stand "aanvraag": zo komt het netjes in de lead te staan.
                        note: this.form.message.trim(),
                        interests: this.interesses,
                        page: window.location.pathname,
                        // Bij direct boeken bepaalt de productgroep bij welke adviseur de afspraak komt.
                        product_group_id: this.productgroep,
                    }),
                });
                const data = await response.json().catch(() => ({}));

                if (response.ok && data.success) {
                    const aanvraag = data.mode === 'aanvraag';

                    this.bevestigd = { datum: this.dag, tijd: this.tijd, naam: this.form.first_name, aanvraag };
                    this.stap = 'bevestigd';

                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({ event: 'showroom_afspraak_geboekt', bron: this.bron || 'knop', modus: aanvraag ? 'aanvraag' : 'direct' });

                    // Direct geboekt: het tijdslot is weg; volgende keer verse beschikbaarheid.
                    if (!aanvraag) {
                        this.status = 'idle';
                        this.dagen = [];
                    }
                    return;
                }

                if (response.status === 422 && data.errors) {
                    this.fout = Object.values(data.errors).flat()[0] ?? t('Controleer uw gegevens.');
                } else if (response.status >= 500 || response.status === 419 || response.status === 429) {
                    // Een storing of verlopen sessie is geen bezet tijdstip: dat zeggen we dan ook niet.
                    this.fout = data.message ?? t('Boeken lukt nu niet. Bel ons gerust op 046 - 808 01 17, dan plannen we het samen in.');
                } else {
                    this.fout = data.message ?? t('Dit tijdstip is net vergeven. Kies een ander moment.');
                }

                // Een vergeven of ongeldig tijdslot: terug naar het moment, met verse beschikbaarheid.
                const slotWeg = response.status === 422
                    && (data.slot_weg || data.errors?.time || data.errors?.date || /geboekt|bezet|beschikbaar|tijdslot/i.test(data.message ?? ''));

                if (slotWeg) {
                    this.stap = 'moment';
                    this.tijd = null;
                    await this.herlaad();
                }
            } catch (e) {
                this.fout = t('Boeken lukt nu niet. Bel ons gerust op 046 - 808 01 17, dan plannen we het samen in.');
            } finally {
                this.bezig = false;
            }
        },

        // Download via een Blob: betrouwbaarder dan een data-URI op de telefoon.
        downloadAgenda() {
            const inhoud = decodeURIComponent(this.agendaLink().split(',')[1] ?? '');
            if (!inhoud) return;

            const blob = new Blob([inhoud], { type: 'text/calendar;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'renoco-showroomafspraak.ics';
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 10000);
        },

        agendaLink() {
            if (!this.bevestigd) return '#';

            const [uur, minuut] = this.bevestigd.tijd.split(':').map(Number);
            const datum = this.bevestigd.datum.replace(/-/g, '');
            const start = `${datum}T${String(uur).padStart(2, '0')}${String(minuut || 0).padStart(2, '0')}00`;
            const eindUur = uur + 1;
            const eind = `${datum}T${String(eindUur).padStart(2, '0')}${String(minuut || 0).padStart(2, '0')}00`;

            const regels = [
                'BEGIN:VCALENDAR',
                'VERSION:2.0',
                'PRODID:-//Renoco//Showroomafspraak//NL',
                'BEGIN:VEVENT',
                `UID:${datum}-${uur}${minuut}-${Date.now()}@renoco.nl`,
                `DTSTAMP:${new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`,
                `DTSTART;TZID=Europe/Amsterdam:${start}`,
                `DTEND;TZID=Europe/Amsterdam:${eind}`,
                `SUMMARY:${icsTekst(t('Showroomafspraak Renoco'))}`,
                `LOCATION:${icsTekst('Renoco showroom, Economiestraat 1A, 6135 KV Sittard')}`,
                `DESCRIPTION:${icsTekst(t("Neem foto's en globale maten van uw woning mee. Gratis parkeren voor de deur."))}`,
                'END:VEVENT',
                'END:VCALENDAR',
            ];

            return `data:text/calendar;charset=utf-8,${encodeURIComponent(regels.join('\r\n'))}`;
        },
    };
}
